import { Component, OnInit, HostBinding, ViewChild, Renderer2, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { SharedService } from '../services/shared.service';
import { HomeService } from '../services/home.service';
import { Constants } from '../constants';
import { ModuleNameEnum, Piwiktrack } from '../shared/piwiktrack';
import { Router } from '@angular/router';
import { mvnfbuAnimations } from '../shared/animations';
import { BackgroundLocalComponent } from '../shared/background-local/background-local.component';
import { LocalNewsModel } from '../typings';
import { takeUntil } from 'rxjs/operators';
import { ActivityLoggerService } from '../services/activityLogger.service';
import { VideoStallHandlerService } from '../services/video-stall-handler.service';
import { LocalNewsBaseComponent } from '../shared/local-news-base.component';

@Component({
  selector: 'app-local',
  templateUrl: './local.component.html',
  styleUrls: ['./local.component.scss'],
  standalone: false,
   animations: mvnfbuAnimations
})
export class LocalComponent extends LocalNewsBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class') class = 'fullWith';
  
  @ViewChild(BackgroundLocalComponent) background: BackgroundLocalComponent;

  public firstAnimationStateOut = false;
  public animationStateIn = false;
  public animationStateOut = false;

  private newsChangeId?: number;
  private finalAnimation: boolean = false;

  // ==================== LIFECYCLE HOOKS ====================
  
  constructor(sharedService: SharedService, homeService: HomeService, private router: Router, private el: ElementRef, private renderer: Renderer2, logger: ActivityLoggerService, videoStallHandlerService: VideoStallHandlerService) {
    super(sharedService, homeService, logger, videoStallHandlerService);
    this.blobUrl = sharedService.blobUrl;
  }

  ngOnInit() {
    this.initializeData();
  }

  ngAfterViewInit(){
    this.initializeBackground();
  }

  override ngOnDestroy(): void {
    console.log("ngOnDestroy LocalComponent");
    super.ngOnDestroy();
    this.clearNextNewsTimeout();
    this.clearVideoTimeout();
    this.clearMetadataTimeout();
    this.cleanupVideoListeners();
  }

  // ==================== MÉTHODES D'INITIALISATION ====================

  public initializeData() {
    this.homeService.getNews()
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (news) => {
        this.clearNextNewsTimeout();
        console.log("Initializing local news data");
        if(news == null) {
          this.homeService.loadNews();
        }
        if (news && news.localNews && news.localNews.length > 0) {  
          console.log("Local news available, initializing component.");
          console.log(news.localNews.length);
          this.localNews = news.localNews;
          this.currentNews = this.localNews[this.currentNewsIndex];
          this.futureNews = this.localNews[this.currentNewsIndex];
          this.initializeBackground();
          this.startingAnimation = false;
          this.firstAnimationStateOut = true;
        }
        else if(news && (news.internalNews && news.internalNews.length > 0)) {
          console.warn("No local news available, redirecting to home.");
          this.sharedService.navigateByDisplayMode('home');
        }
        this.withOtherNews = (news?.internalNews && news.internalNews.length > 0) ?? false;
      }
      ,
        error: (e: any) => {
          console.error(e);
          this.sharedService.navigate([Constants.RedirectUrl.Error]);
        }
      });
      this.sharedService.getToken().then(token => {
        Piwiktrack.piwikTrackPage(this.homeService.uuid, ModuleNameEnum.ScreenView, token);
      });
  }

  private initializeBackground(): void {
    if(this.background)
      this.background.setInitialValue(undefined, this.currentNews?.background?.image);
  }

  // ==================== NAVIGATION ENTRE LES NEWS ====================

  protected moveToNextNews(): void {
    console.log('moveToNextNews');
    this.clearNextNewsTimeout();
    this.clearVideoTimeout();
    this.clearMetadataTimeout();
    this.videoReady = false;
    this.videoIsPlaying = false;

    this.isTransitionSafetyToSafety = this.currentNews?.tag === 'safety' && this.isTheNextNewsSafety();
    this.currentNewsIndex = (this.count + 1) % this.localNews.length;
    this.count++;
    if((this.count === 5 || this.count >= this.localNews.length)){ 
      if(this.withOtherNews){
        console.log("Final animation to other news");
        this.firstAnimationStateOut = true;
        this.finalAnimation = true;
        this.futureNews = undefined;
      }
      else{
        this.count = 0;
        this.currentNewsIndex = 0;
        this.futureNews = this.localNews[this.currentNewsIndex];
        this.firstAnimationStateOut = true;
        this.homeService.loadNews();
        this.sharedService.navigateByDisplayMode('local');
      }
    }
    else{
      this.futureNews = this.localNews[this.currentNewsIndex];
      this.firstAnimationStateOut = true;
    }
  }

  private setTimerForNextNews(): void {
    const timing = this.getNewsDisplayDuration();
    this.clearNextNewsTimeout();
    console.log('setTimerForNextNews');
    this.newsChangeId = window.setTimeout(() => {
      this.moveToNextNews();
    }, timing);
  }

  // ==================== GESTION DES ANIMATIONS ====================

  public animOutTagDone() {
    if( this.firstAnimationStateOut && !this.futureNews && !this.finalAnimation){
      setTimeout(() => {
        console.log("No future news, resetting to first news");
         this.animationStateOut = true;
      }, 200);
    }
    else if ( this.firstAnimationStateOut ) {
      console.log("animOutTagDone - proceeding to next news");
      this.animationStateOut = true;
      if(!this.finalAnimation && this.futureNews && this.futureNews.background?.image)
        this.background.changeValue('',this.futureNews.background?.image);
      else if (this.finalAnimation) {
          setTimeout(() => {
            console.log("Navigating to home after final animation");
            this.clearVideoTimeout(); 
            this.clearMetadataTimeout();
            this.sharedService.navigateByDisplayMode('home');
          }, 400);
      }
    }
  }

  public animOutDone() {
    if(this.animationStateOut){
      console.log("animOutDone");
      if (this.futureNews) {
        this.currentNews = this.futureNews;
      }
      this.animationStateOut = false;
      this.animationStateIn = true;
      this.firstAnimationStateOut = false;
    }
  }

  public animInDone() {
    if(this.animationStateIn){
      console.log("animInDone");
      this.animationStateIn = false;
      if (this.currentNews && this.currentNews.video) {
        this.displayStreamVideo();
      } else {
        this.setTimerForNextNews();
      }
    }
  }

  // ==================== NETTOYAGE ====================

  private clearNextNewsTimeout(): void {
    this.newsChangeId = this.clearTimer(this.newsChangeId);
  }
}